import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import LoadingMask from '../../CommonComponents/LoadingMask';
import { insuranceSelector } from '../MoneyGuidProSelectors';
import { insuranceReducerPrefix } from '../MoneyGuideProConstants';
import { formatNumberLegacy } from '../../CommonUtils/formatNumberLegacy';
import { TabProps } from '../MoneyGuideProTypes';
import {
  CenterAlignedCell,
  FooterTableCell,
  FooterTableRightAlignedCell,
  HeaderTableCell,
  RightAlignedCell,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from './InsuranceStyles';
import { Tab } from '../Tab/Tab';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { PDFSafeTableHeaderCaption } from '../../CommonComponents/OPL/HtmlTable/components/PDFSafeTableHeaderCaption/PDFSafeTableHeaderCaption';

const InsuranceTableHeaderRow = () => (
  <TableRow>
    <HeaderTableCell>Description</HeaderTableCell>
    <HeaderTableCell>Owner</HeaderTableCell>
    <HeaderTableCell>Insured</HeaderTableCell>
    <HeaderTableCell style={{ minWidth: '200px' }}>Beneficiaries</HeaderTableCell>
    <HeaderTableCell style={{ textAlign: 'right' }}>Annual Premium</HeaderTableCell>
    <HeaderTableCell style={{ textAlign: 'right' }}>Cash Value</HeaderTableCell>
    <HeaderTableCell style={{ textAlign: 'right' }}>Death Benefit</HeaderTableCell>
    <HeaderTableCell>Premium Paid</HeaderTableCell>
  </TableRow>
);

export const Insurance: React.FC<TabProps> = ({ id, clientId }) => {
  const data = useSelector(insuranceSelector);

  const LoadingState = useMemo(
    () => (
      <>
        <LoadingMask width="100%" height="200px" isDark />
        <br />
        <LoadingMask width="100%" height="50px" isDark />
      </>
    ),
    [],
  );

  const EmptyState = useMemo(
    () => (
      <Table style={{ background: '#fff' }}>
        <TableHeader isFirst>
          <InsuranceTableHeaderRow />
          <PDFSafeTableHeaderCaption colSpan={8}>
            <Text
              color="neutral.600"
              textAlign="left"
              fontSize="h6"
              fontWeight={600}
              py="16"
              px="12"
            >
              Insurance Policies
            </Text>
          </PDFSafeTableHeaderCaption>
        </TableHeader>
        <TableBody>
          <PDFSafeTableHeaderCaption colSpan={8}>
            <Text textAlign="center" py="16">
              <strong>Total Death Benefit of All Policies</strong>
            </Text>
          </PDFSafeTableHeaderCaption>
        </TableBody>
      </Table>
    ),
    [],
  );

  return (
    <Tab
      tabTitle="Insurance Policies"
      mgpItemId={id}
      clientId={clientId}
      asOfDate={data ? data.asOfDate : null}
      reducerPrefix={insuranceReducerPrefix}
      isEmpty={data === null || data?.policyGroups?.length === 0}
      isPristine={data === undefined}
      LoadingState={LoadingState}
      EmptyState={EmptyState}
    >
      {data ? (
        <Table>
          {data.policyGroups.map((group, policyGroupIndex) => (
            <React.Fragment key={group.groupName}>
              <TableHeader isFirst={policyGroupIndex === 0}>
                {policyGroupIndex === 0 && <InsuranceTableHeaderRow />}
                <PDFSafeTableHeaderCaption colSpan={8}>
                  <Text
                    color="neutral.600"
                    textAlign="left"
                    fontSize="h6"
                    fontWeight={600}
                    py="16"
                    px="12"
                  >
                    {group.groupName}
                  </Text>
                </PDFSafeTableHeaderCaption>
              </TableHeader>
              <TableBody>
                {group.policies.map(policy => (
                  <TableRow style={{ background: '#fff' }} key={policy.description}>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{policy.description}</TableCell>
                    <TableCell>{policy.owner}</TableCell>
                    <TableCell>{policy.insured}</TableCell>
                    <TableCell>
                      {policy.beneficiaries &&
                        policy.beneficiaries.map(beneficiary => (
                          <p key={beneficiary}>{beneficiary}</p>
                        ))}
                    </TableCell>
                    <RightAlignedCell>
                      {formatNumberLegacy(policy.annualPremium, {
                        type: 'currency',
                      })}
                    </RightAlignedCell>
                    <RightAlignedCell>
                      {formatNumberLegacy(policy.cashValue, {
                        type: 'currency',
                      })}
                    </RightAlignedCell>
                    <RightAlignedCell>
                      {formatNumberLegacy(policy.deathBenefit, {
                        type: 'currency',
                      })}
                    </RightAlignedCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{policy.premiumPaid}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </React.Fragment>
          ))}
          <TableBody>
            <TableRow>
              <FooterTableCell />
              <FooterTableCell />
              <FooterTableCell />
              <FooterTableCell />
              <FooterTableCell
                colSpan={2}
                style={{
                  borderLeft: '1px solid #d9d7d7',
                  borderBottom: '1px solid #d9d7d7',
                  borderBottomLeftRadius: '4px',
                  whiteSpace: 'nowrap',
                  background: '#fff',
                }}
              >
                <strong>Total Death Benefit of All Policies</strong>
              </FooterTableCell>
              <FooterTableRightAlignedCell
                style={{ borderBottom: '1px solid #d9d7d7', background: '#fff' }}
              >
                <strong>
                  {formatNumberLegacy(data.totalDeathBenefit, {
                    type: 'currency',
                  })}
                </strong>
              </FooterTableRightAlignedCell>
              <FooterTableCell
                style={{
                  borderBottom: '1px solid #d9d7d7',
                  borderBottomRightRadius: '4px',
                  background: '#fff',
                }}
              />
            </TableRow>
          </TableBody>
        </Table>
      ) : null}
    </Tab>
  );
};
